import {
  EditOutlined,
  FileSearchOutlined,
  SaveOutlined,
  StopOutlined,
  SwapOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import type { ColumnsType, PaginatedFilteredTableRef } from '@components/PaginatedFilteredTable';
import PaginatedFilteredTable from '@components/PaginatedFilteredTable';
import { useCooldown } from '@hooks/use-cooldown';
import { languages } from '@interfaces/language';
import type { TelegramGroup } from '@interfaces/telegram-group';
import useEditableForm from '@pages/Management/hooks/use-editable-form';
import type { LoaderData } from '@services/types/loader-data';
import { Alert, Badge, Button, Form, message, Space, Tooltip } from 'antd';
import type { ParsedQs } from 'qs';
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Link, useLoaderData } from 'react-router-dom';

import type { EditableCellProps } from '../EditableCell/EditableCell';
import EditableCell from '../EditableCell/EditableCell';
import { ACTIONS } from './action';
import BulkMessageForm from './BulkMessageForm';
import styles from './ConversationManagement.module.scss';
import CreateConversationForm from './CreateConversationForm';
import type { loader } from './loader';
import TransferOwnershipForm from './TransferOwnershipForm';

interface ConversationForm extends Record<string, unknown> {
  name: string;
  language_code: string;
  is_authorized: boolean;
}

const MILLISECONDS_IN_MINUTE = 60 * 1000;
const SYNC_COOLDOWN_IN_MINUTES = 30;

const ConversationManagement: FC = () => {
  const loaderData = useLoaderData() as LoaderData<typeof loader>;
  const [data, setData] = useState(loaderData);
  useEffect(() => {
    setData(loaderData);
  }, [loaderData]);

  const [isCreateConversationModalOpen, setCreateConversationModalOpen] = useState(false);
  const [isBulkMessageModalOpen, setBulkMessageModalOpen] = useState(false);
  const [transferableConversationId, setTransferableConversationId] = useState<number>();

  const tableRef = useRef<PaginatedFilteredTableRef>(null);
  const [appliedFilters, setAppliedFilters] = useState<ParsedQs>();
  const [isSyncOnCooldown, toggleSyncCooldown] = useCooldown(
    'last-conversation-names-sync',
    SYNC_COOLDOWN_IN_MINUTES * MILLISECONDS_IN_MINUTE,
  );

  const {
    form,
    isLoadingOrSubmitting,
    isRecordEdited,
    cancelEditing,
    setRecordEditable,
    submitForm,
    submitFunction,
    error: formError,
    resetFetcher,
  } = useEditableForm<TelegramGroup, ConversationForm>(
    'id',
    ['name', 'is_authorized', 'language_code'],
    'conversation-fetcher',
    true,
  );

  const [messageApi, messagesContextHolder] = message.useMessage();

  const columns: ColumnsType<TelegramGroup> = [
    {
      key: 'id',
      title: 'Telegram group ID',
      dataIndex: 'id',
    },
    {
      key: 'name',
      title: 'Conversation name',
      dataIndex: 'name',
      filterable: true,
      onCell: (record): Omit<EditableCellProps, 'children'> => ({
        dataIndex: 'name',
        inputType: 'text',
        title: 'Name',
        editing: isRecordEdited(record),
        max: 96,
      }),
    },
    {
      key: 'language_code',
      title: 'Language',
      dataIndex: 'language_code',
      filters: Object.entries(languages).map(([key, value]) => ({
        value: key,
        text: value,
      })),
      filterSearch: true,
      filterMultiple: false,
      onCell: (record): Omit<EditableCellProps, 'children'> => ({
        dataIndex: 'language_code',
        inputType: 'select',
        selectOptions: Object.entries(languages).map(([key, value]) => ({
          value: key,
          label: value,
        })),
        title: 'Language',
        editing: isRecordEdited(record),
      }),
      render: (value: string) => {
        return value in languages ? languages[value] : value;
      },
    },
    {
      key: 'is_authorized',
      title: 'Authorized',
      dataIndex: 'is_authorized',
      filters: [
        {
          value: 'true',
          text: 'Authorized',
        },
        {
          value: 'false',
          text: 'Unauthorized',
        },
      ],
      filterMultiple: false,
      onCell: (record): Omit<EditableCellProps, 'children'> => ({
        dataIndex: 'is_authorized',
        inputType: 'switch',
        title: 'Authorized',
        editing: isRecordEdited(record),
        required: false,
      }),
      render: (_, record) => (
        <Badge status={record.is_authorized ? 'success' : 'error'} text={record.is_authorized ? 'Yes' : 'No'} />
      ),
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, record) => {
        const editable = isRecordEdited(record);
        return (
          <Space size="middle">
            {editable ? (
              <>
                <Button type="primary" icon={<SaveOutlined />} title="Save" onClick={() => submitForm(record)} />
                <Button
                  type="default"
                  icon={<StopOutlined />}
                  title="Cancel"
                  onClick={() => {
                    resetFetcher();
                    cancelEditing();
                  }}
                />
              </>
            ) : (
              <>
                <Button type="default" icon={<EditOutlined />} title="Edit" onClick={() => setRecordEditable(record)} />
                <Button
                  type="default"
                  disabled={!record.is_authorized}
                  icon={<SyncOutlined />}
                  title="Update the conversation name to match the Telegram group name"
                  onClick={() => submitForm({ id: record.id }, ACTIONS.SyncName, undefined, 'POST')}
                />
                <Button
                  type="default"
                  icon={<SwapOutlined />}
                  title="Transfer ownership"
                  onClick={() => setTransferableConversationId(record.id)}
                />
                <Link to={`/chat/conversation/${record.id}`} target="_blank" rel="noopener noreferrer">
                  <Button type="default" icon={<FileSearchOutlined />} title="Review messages" />
                </Link>
              </>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <div className={styles.buttons}>
        <Tooltip title="Send bulk message to all conversations. Use the conversation filter function to message a specific subset.">
          <Button
            type="default"
            onClick={() => {
              setBulkMessageModalOpen(true);
            }}
            disabled={isLoadingOrSubmitting}
          >
            Bulk Message
          </Button>
        </Tooltip>
        <Tooltip
          title={`Update all conversation names to match the Telegram group names (allowed once every ${SYNC_COOLDOWN_IN_MINUTES} minutes)`}
        >
          <Button
            type="default"
            icon={<SyncOutlined spin={isSyncOnCooldown} />}
            disabled={isLoadingOrSubmitting || isSyncOnCooldown}
            onClick={() => {
              toggleSyncCooldown();
              submitFunction(
                {
                  intent: ACTIONS.SyncAllNames,
                },
                {
                  method: 'POST',
                  encType: 'application/json',
                },
              );
            }}
          >
            Update All Names
          </Button>
        </Tooltip>
        <Button
          type="primary"
          onClick={() => {
            setCreateConversationModalOpen(true);
          }}
          disabled={isLoadingOrSubmitting}
        >
          Create Conversation
        </Button>
      </div>
      <BulkMessageForm
        open={isBulkMessageModalOpen}
        onCancel={() => {
          setBulkMessageModalOpen(false);
        }}
        onSent={() => {
          setBulkMessageModalOpen(false);
          // noinspection JSIgnoredPromiseFromCall
          messageApi.info('Sending message queued.');
        }}
        filters={appliedFilters}
        isFormDisabled={data.data.length === 0}
      />
      <CreateConversationForm
        open={isCreateConversationModalOpen}
        onCreate={() => setCreateConversationModalOpen(false)}
        onCancel={() => setCreateConversationModalOpen(false)}
      />
      <TransferOwnershipForm
        onCancel={() => setTransferableConversationId(undefined)}
        onTransfer={() => setTransferableConversationId(undefined)}
        conversationId={transferableConversationId}
      />
      <Form form={form} component={false}>
        {formError && (
          <Alert
            message={formError}
            closable
            onClose={() => resetFetcher()}
            style={{ margin: '24px 0' }}
            type="error"
          />
        )}
        <PaginatedFilteredTable<TelegramGroup>
          ref={tableRef}
          data={data}
          columns={columns}
          rowKey="id"
          loading={isLoadingOrSubmitting}
          rowClassName="editable-row"
          components={{ body: { cell: EditableCell } }}
          onFiltersChange={(filters) => setAppliedFilters(filters)}
        />
      </Form>
      {messagesContextHolder}
    </>
  );
};

export default ConversationManagement;
